/**
 * The zoom/pan VIEWPORT around a {@link MapCanvas} (Big Phase 1.5 UI). A thin
 * React wrapper that captures wheel (zoom to cursor) + pointer drag (pan),
 * clamps both via the pure {@link ./viewport} math, and feeds the resulting SVG
 * `transform` string to the canvas. Shared by the inline mini-map and the
 * full-screen viewer so behavior is identical in both.
 *
 * The transform math is PURE + unit-tested in `viewport.ts`; this component only
 * wires DOM events to it + exposes zoom-in / zoom-out / fit controls (which also
 * make zoom usable without a wheel, e.g. touch/keyboard).
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { SpatialMapGraph } from "@rufus/shared-types";

import { MapCanvas } from "./MapCanvas.js";
import {
  dragPan,
  fit,
  toTransform,
  wheelZoom,
  type Size,
  type ViewportState,
  IDENTITY,
} from "./viewport.js";

export interface MapViewportProps {
  readonly map: SpatialMapGraph;
  readonly currentNodeId?: string;
  readonly ariaLabel?: string;
  /** Zoom step per wheel notch / button press. */
  readonly zoomStep?: number;
}

/** The wheel-zoom factor per notch (in) / its inverse (out). */
const DEFAULT_ZOOM_STEP = 1.2;

export function MapViewport({ map, currentNodeId, ariaLabel = "Map", zoomStep = DEFAULT_ZOOM_STEP }: MapViewportProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [view, setView] = useState<ViewportState>(IDENTITY);
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const dragRef = useRef<{ x: number; y: number } | null>(null);

  // Measure the host so the pan clamp knows the viewport extent. ResizeObserver
  // keeps it correct when the mini-map expands to full screen.
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = () => setSize({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const cursorOf = useCallback((e: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = hostRef.current?.getBoundingClientRect();
    return { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) };
  }, []);

  // The current viewport size + zoom step, held in a ref so the native wheel
  // listener (bound ONCE below) always reads the latest values without rebinding
  // on every resize. Rebinding a passive:false listener per render is wasteful
  // and risks a missed cleanup; a ref keeps the effect's dep list empty.
  const liveRef = useRef({ size, zoomStep });
  liveRef.current = { size, zoomStep };

  // Wheel-zoom via a NATIVE non-passive listener (NOT React's `onWheel`). React
  // attaches `onWheel` as a PASSIVE listener, so calling `e.preventDefault()` in
  // it is rejected by the browser and floods the console with "Unable to
  // preventDefault inside passive event listener invocation". Binding our own
  // listener with `{ passive: false }` lets us legitimately prevent the page from
  // scrolling while zooming the map. Bound once; cleaned up on unmount.
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const { size: curSize, zoomStep: curStep } = liveRef.current;
      const factor = e.deltaY < 0 ? curStep : 1 / curStep;
      setView((v) => wheelZoom(v, factor, cursorOf(e), curSize));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [cursorOf]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const start = dragRef.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      dragRef.current = { x: e.clientX, y: e.clientY };
      setView((v) => dragPan(v, dx, dy, size));
    },
    [size],
  );

  const endDrag = useCallback((e: React.PointerEvent) => {
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  }, []);

  const zoomInBtn = useCallback(() => {
    const c = { x: size.width / 2, y: size.height / 2 };
    setView((v) => wheelZoom(v, zoomStep, c, size));
  }, [size, zoomStep]);
  const zoomOutBtn = useCallback(() => {
    const c = { x: size.width / 2, y: size.height / 2 };
    setView((v) => wheelZoom(v, 1 / zoomStep, c, size));
  }, [size, zoomStep]);
  const fitBtn = useCallback(() => setView(fit()), []);

  return (
    <div className="map-viewport" data-testid="map-viewport" style={{ position: "relative", width: "100%", height: "100%" }}>
      <div
        ref={hostRef}
        className="map-viewport-surface"
        data-testid="map-viewport-surface"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={endDrag}
        style={{ touchAction: "none", cursor: dragRef.current ? "grabbing" : "grab", width: "100%", height: "100%" }}
      >
        <MapCanvas map={map} currentNodeId={currentNodeId} transform={toTransform(view)} ariaLabel={ariaLabel} />
      </div>
      <div className="map-viewport-controls" role="group" aria-label="Map zoom controls">
        <button type="button" onClick={zoomInBtn} aria-label="Zoom in" data-testid="map-zoom-in">+</button>
        <button type="button" onClick={zoomOutBtn} aria-label="Zoom out" data-testid="map-zoom-out">−</button>
        <button type="button" onClick={fitBtn} aria-label="Reset zoom" data-testid="map-zoom-fit">⤢</button>
      </div>
    </div>
  );
}
